import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queryKeys";
import { playSfx } from "@/platform";
import { useRealtimeCollection } from "@/lib/realtime";
import {
  deleteInboxItem,
  fetchInboxItems,
  fetchInboxSettings,
  rotateInboxToken,
  setInboxStatus,
  setInboxToken,
  type InboxSettings,
} from "./api";
import type { InboxItem, InboxStatus } from "./model";

/**
 * Data access for the inbox: two queries, four mutations, one subscription.
 *
 * Simpler than `features/tasks/hooks.ts` on purpose, and the difference is worth
 * naming. Tasks defers realtime invalidation while its own writes are in flight
 * because a five-row drag produces five overlapping writes whose intermediate
 * orders would flicker back. The inbox has no ordering and no bulk write: every
 * mutation here touches exactly one record and settles in one round trip, so
 * plain invalidation cannot produce that class of flicker and the machinery
 * would be complexity with nothing to hold up.
 *
 * The realtime subscription matters more here than anywhere else in the app,
 * because this is the one collection that gets written to by *somebody else*.
 * An item dropped off by a colleague has to appear without a refresh — that is
 * the whole promise of the screen.
 */

export function inboxItemsKey(): QueryKey {
  return queryKeys.collection("inbox_items", "all");
}

export function inboxSettingsKey(): QueryKey {
  return queryKeys.collection("inbox_settings", "single");
}

export function useInboxItems() {
  return useQuery({
    queryKey: inboxItemsKey(),
    queryFn: ({ signal }) => fetchInboxItems({ signal }),
    // Universal search may have primed a fresh empty cache before an external
    // sender posted. Entering Inbox is a deliberate check for new mail.
    refetchOnMount: "always",
  });
}

/** Keep the list in step with what other people are dropping off. */
export function useInboxRealtime(): void {
  useRealtimeCollection("inbox_items");
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Mark an item read, unread or archived.
 *
 * Optimistic, because the common case is a keyboard-speed sweep down a list of
 * fifteen things: waiting for a round trip per item would make triage feel like
 * filing. The cache is patched in place and reconciled on settle.
 */
export function useSetInboxStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ item, status }: { item: InboxItem; status: InboxStatus }) =>
      setInboxStatus(item, status),
    onMutate: async ({ item, status }) => {
      await queryClient.cancelQueries({ queryKey: inboxItemsKey() });
      const previous = queryClient.getQueryData<InboxItem[]>(inboxItemsKey());
      queryClient.setQueryData<InboxItem[]>(inboxItemsKey(), (items) =>
        (items ?? []).map((entry) =>
          entry.id === item.id
            ? {
                ...entry,
                status,
                // Mirrors the rule in `api.setInboxStatus` so the optimistic row
                // and the server's answer say the same thing about when this was
                // first seen.
                readAt: status !== "new" && !entry.readAt ? new Date().toISOString() : entry.readAt,
              }
            : entry,
        ),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(inboxItemsKey(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collection("inbox_items") });
    },
  });
}

/**
 * Delete an item outright.
 *
 * Archiving is the ordinary end of an item's life; this is for spam, and for the
 * duplicate that arrives when somebody's script retries. There is no undo — the
 * record and its attachments are gone from the server — so the call site
 * confirms first.
 */
export function useDeleteInboxItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInboxItem(id),
    onMutate: async (id) => {
      playSfx("delete");
      await queryClient.cancelQueries({ queryKey: inboxItemsKey() });
      const previous = queryClient.getQueryData<InboxItem[]>(inboxItemsKey());
      queryClient.setQueryData<InboxItem[]>(inboxItemsKey(), (items) =>
        (items ?? []).filter((entry) => entry.id !== id),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(inboxItemsKey(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collection("inbox_items") });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* The shared token                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The shared token.
 *
 * `enabled` is how the inbox screen keeps the token off the wire until the API
 * panel is actually opened: a secret that is fetched on every page view is a
 * secret sitting in the query cache, and in a devtools network log, for a screen
 * that usually has nothing to do with it.
 */
export function useInboxSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: inboxSettingsKey(),
    queryFn: ({ signal }) => fetchInboxSettings({ signal }),
    enabled: options.enabled ?? true,
    // The token changes only when this app changes it, and the mutation below
    // writes the answer straight into the cache.
    staleTime: Infinity,
  });
}

export function useRotateInboxToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rotateInboxToken(id),
    onSuccess: (settings: InboxSettings) => {
      queryClient.setQueryData(inboxSettingsKey(), settings);
    },
  });
}

/**
 * Set the token to something the owner chose.
 *
 * Same cache write as a rotation, because it is the same thing from the
 * endpoint's point of view: the previous value stops working immediately.
 */
export function useSetInboxToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, token }: { id: string; token: string }) => setInboxToken(id, token),
    onSuccess: (settings: InboxSettings) => {
      queryClient.setQueryData(inboxSettingsKey(), settings);
    },
  });
}
